import { FilterFn } from '@tanstack/react-table'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import {
	ColumnFilterType,
	DataTableEntry,
	ContactTableEntry,
	OrderByType,
	OrderDirectionType,
} from '../../pages/Workspace/components/interfaces.table'
import { useCallback } from 'react'
import type { AdminUserFilterType } from '../../lib/api/types/organization.interfaces'
import { isInviteExpired } from '../../lib/utils/isInviteExpired'

export const FILTER_TYPES = {
	CONTAINS: 0,
	STARTS_WITH: 1,
	ENDS_WITH: 2,
	LESS_THAN: 3,
	MORE_THAN: 4,
	BETWEEN: 5,
	EXACT: 6,
	OBJECT_ID: 7,
	NUMBER: 8,
	STRING: 9,
} as const

export const FILTER_LABELS = {
	[FILTER_TYPES.CONTAINS]: 'Contains',
	[FILTER_TYPES.STARTS_WITH]: 'Starts with',
	[FILTER_TYPES.ENDS_WITH]: 'Ends with',
	[FILTER_TYPES.LESS_THAN]: 'Less than',
	[FILTER_TYPES.MORE_THAN]: 'Greater than',
	[FILTER_TYPES.BETWEEN]: 'Between',
	[FILTER_TYPES.EXACT]: 'Exact',
} as const

export interface SelectOption {
	value: number
	label: string
}

export const TEXT_FILTER_OPTIONS: SelectOption[] = [
	{ value: FILTER_TYPES.CONTAINS, label: FILTER_LABELS[FILTER_TYPES.CONTAINS] },
	{
		value: FILTER_TYPES.STARTS_WITH,
		label: FILTER_LABELS[FILTER_TYPES.STARTS_WITH],
	},
	{
		value: FILTER_TYPES.ENDS_WITH,
		label: FILTER_LABELS[FILTER_TYPES.ENDS_WITH],
	},
]

export const NUMERIC_FILTER_OPTIONS: SelectOption[] = [
	{
		value: FILTER_TYPES.LESS_THAN,
		label: FILTER_LABELS[FILTER_TYPES.LESS_THAN],
	},
	{
		value: FILTER_TYPES.MORE_THAN,
		label: FILTER_LABELS[FILTER_TYPES.MORE_THAN],
	},
	{ value: FILTER_TYPES.BETWEEN, label: FILTER_LABELS[FILTER_TYPES.BETWEEN] },
	{ value: FILTER_TYPES.EXACT, label: FILTER_LABELS[FILTER_TYPES.EXACT] },
]

dayjs.extend(isBetween)

const SORT_DIRECTIONS: OrderDirectionType[] = ['asc', 'desc', null]

export function useSorting(
	orderBy: OrderByType,
	orderDirection: OrderDirectionType,
	onChange: (orderBy: OrderByType, direction: OrderDirectionType) => void
) {
	const toggleSorting = useCallback(
		(columnId: OrderByType) => {
			let newDirection: OrderDirectionType = 'asc'
			let newOrderBy: OrderByType = columnId

			if (columnId === orderBy) {
				const currentIndex = SORT_DIRECTIONS.indexOf(orderDirection)
				const nextIndex = (currentIndex + 1) % SORT_DIRECTIONS.length
				newDirection = SORT_DIRECTIONS[nextIndex]

				if (newDirection === null) {
					newOrderBy = null
				}
			}

			onChange(newOrderBy, newDirection)
		},
		[orderBy, orderDirection, onChange]
	)

	return {
		orderBy,
		orderDirection,
		toggleSorting,
	}
}

export const useCustomGlobalFilter = (): FilterFn<any> => {
	return (row, _columnId, filterValue) => {
		const excludedKeys = ['options']
		return row
			.getAllCells()
			.filter((cell) => !excludedKeys.includes(cell.column.id))
			.some((cell) => {
				const value = cell.getValue()
				if (typeof value === 'string') {
					return value.toLowerCase().includes(filterValue.toLowerCase())
				}
				return false
			})
	}
}

export const useDateFilter = (): FilterFn<DataTableEntry> => {
	return (row, columnId, filterValue) => {
		if (!filterValue || typeof filterValue !== 'object') return true

		const { type, value } = filterValue
		const cellRawValue = row.getValue(columnId) as string
		if (!cellRawValue || !dayjs(cellRawValue).isValid()) return false

		const cellDate = dayjs(cellRawValue)

		if (type === FILTER_TYPES.BETWEEN && typeof value === 'object') {
			const startDate =
				value.startValue && dayjs(value.startValue).isValid()
					? dayjs(value.startValue)
					: null
			const endDate =
				value.endValue && dayjs(value.endValue).isValid()
					? dayjs(value.endValue)
					: null

			return startDate && endDate
				? cellDate.isBetween(startDate, endDate, 'day', '[]')
				: false
		}

		switch (type) {
			case FILTER_TYPES.LESS_THAN:
				return cellDate.isBefore(dayjs(value), 'day')
			case FILTER_TYPES.MORE_THAN:
				return cellDate.isAfter(dayjs(value), 'day')
			case FILTER_TYPES.EXACT:
				return cellDate.isSame(dayjs(value), 'day')
			default:
				return true
		}
	}
}

export const useSizeFilter = (): FilterFn<DataTableEntry> => {
	return (row, _, filterValue) => {
		if (!filterValue || typeof filterValue !== 'object') return true

		const { type, value } = filterValue
		const cellRawValue = row.original?.size
		if (!cellRawValue || isNaN(Number(cellRawValue))) return false

		const cellSize = Number(cellRawValue)

		if (type === FILTER_TYPES.BETWEEN && typeof value === 'object') {
			const startSize = value.startValue ? Number(value.startValue) : null
			const endSize = value.endValue ? Number(value.endValue) : null

			return startSize !== null && endSize !== null
				? cellSize >= startSize && cellSize <= endSize
				: false
		}

		const filterSize = Number(value)

		switch (type) {
			case FILTER_TYPES.LESS_THAN:
				return cellSize < filterSize
			case FILTER_TYPES.MORE_THAN:
				return cellSize > filterSize
			case FILTER_TYPES.EXACT:
				return cellSize === filterSize
			default:
				return false
		}
	}
}

export const useTextFilter = (): FilterFn<DataTableEntry> => {
	return (row, columnId, filterValue) => {
		if (!filterValue || typeof filterValue !== 'object') return true

		const { type, value } = filterValue as {
			type: ColumnFilterType
			value: string
		}
		const cellValue = String(row.getValue(columnId) ?? '').toLowerCase()
		const filterText = value.toLowerCase()

		switch (type) {
			case FILTER_TYPES.CONTAINS:
				return cellValue.includes(filterText)
			case FILTER_TYPES.STARTS_WITH:
				return cellValue.startsWith(filterText)
			case FILTER_TYPES.ENDS_WITH:
				return cellValue.endsWith(filterText)
			default:
				return true
		}
	}
}

export const useDateFilterContact = (): FilterFn<ContactTableEntry> => {
	return (row, columnId, filterValue) => {
		if (!filterValue || typeof filterValue !== 'object') return true

		const { type, value } = filterValue
		const cellRawValue = row.getValue(columnId) as string
		if (!cellRawValue || !dayjs(cellRawValue).isValid()) return false

		const cellDate = dayjs(cellRawValue)

		if (type === FILTER_TYPES.BETWEEN && typeof value === 'object') {
			const startDate =
				value.startValue && dayjs(value.startValue).isValid()
					? dayjs(value.startValue)
					: null
			const endDate =
				value.endValue && dayjs(value.endValue).isValid()
					? dayjs(value.endValue)
					: null

			return startDate && endDate
				? cellDate.isBetween(startDate, endDate, 'day', '[]')
				: false
		}

		switch (type) {
			case FILTER_TYPES.LESS_THAN:
				return cellDate.isBefore(dayjs(value), 'day')
			case FILTER_TYPES.MORE_THAN:
				return cellDate.isAfter(dayjs(value), 'day')
			case FILTER_TYPES.EXACT:
				return cellDate.isSame(dayjs(value), 'day')
			default:
				return true
		}
	}
}

export const useSizeFilterContact = (): FilterFn<ContactTableEntry> => {
	return (row, _, filterValue) => {
		if (!filterValue || typeof filterValue !== 'object') return true

		const { type, value } = filterValue
		const cellRawValue = row.original?.size
		if (!cellRawValue || isNaN(Number(cellRawValue))) return false

		const cellSize = Number(cellRawValue)

		if (type === FILTER_TYPES.BETWEEN && typeof value === 'object') {
			const startSize = value.startValue
				? Number(value.startValue) * 1024
				: null
			const endSize = value.endValue ? Number(value.endValue) * 1024 : null

			return startSize !== null && endSize !== null
				? cellSize >= startSize && cellSize <= endSize
				: false
		}

		const filterSize = Number(value) * 1000

		switch (type) {
			case FILTER_TYPES.LESS_THAN:
				return cellSize < filterSize
			case FILTER_TYPES.MORE_THAN:
				return cellSize > filterSize
			case FILTER_TYPES.EXACT:
				return cellSize === filterSize
			default:
				return true
		}
	}
}

export const useTextFilterContact = (): FilterFn<ContactTableEntry> => {
	return (row, columnId, filterValue) => {
		if (!filterValue || typeof filterValue !== 'object') return true

		const { type, value } = filterValue as {
			type: ColumnFilterType
			value: string
		}
		const cellValue = String(row.getValue(columnId) ?? '').toLowerCase()
		const filterText = value.toLowerCase()

		switch (type) {
			case FILTER_TYPES.CONTAINS:
				return cellValue.includes(filterText)
			case FILTER_TYPES.STARTS_WITH:
				return cellValue.startsWith(filterText)
			case FILTER_TYPES.ENDS_WITH:
				return cellValue.endsWith(filterText)
			default:
				return true
		}
	}
}

export const useObjectIdFilter = (): FilterFn<DataTableEntry> => {
	return (row, columnId, filterValue) => {
		if (!filterValue || typeof filterValue !== 'object') return true

		const { type, value } = filterValue as {
			type: ColumnFilterType
			value: string | string[]
		}

		if (type === FILTER_TYPES.OBJECT_ID) {
			const filterValues = Array.isArray(value) ? value : [value]

			if (['createdBy', 'modifiedBy'].includes(columnId)) {
				const objectKey = `${columnId}Object` as keyof DataTableEntry
				const user = row.original[objectKey] as any
				const userId = user?.id || user
				return filterValues.includes(String(userId))
			}

			if (['category', 'subcategory'].includes(columnId)) {
				if (row.original.objectType === 1) {
					return false
				}

				const objectKey = `${columnId}Object` as keyof DataTableEntry
				const category = row.original[objectKey] as any
				const categoryId = category?.id || category

				if (filterValues.includes('general_document')) {
					const hasNoCategory = !category || !categoryId
					return hasNoCategory || filterValues.includes(String(categoryId))
				}

				return filterValues.includes(String(categoryId))
			}

			const cellValue = String(row.getValue(columnId) ?? '')
			return filterValues.some((filterVal) =>
				cellValue.toLowerCase().includes(String(filterVal).toLowerCase())
			)
		}

		return true
	}
}

export const useTypeFilter = (): FilterFn<DataTableEntry> => {
	return (row, columnId, filterValue) => {
		if (!filterValue || typeof filterValue !== 'object') return true

		const { type, value } = filterValue as {
			type: ColumnFilterType
			value: string
		}

		if (type === FILTER_TYPES.CONTAINS) {
			const filterValue = String(value).toLowerCase()

			if (filterValue === 'folder') {
				return row.original.objectType === 1
			}

			if (filterValue === 'file') {
				return row.original.objectType === 0
			}

			const fileType = row.original.type?.toLowerCase() || ''
			return fileType.includes(filterValue)
		}

		return true
	}
}

export const useStatusFilter = (): FilterFn<DataTableEntry> => {
	return (row, columnId, filterValue) => {
		if (!filterValue || typeof filterValue !== 'object') return true

		const { type, value } = filterValue as {
			type: ColumnFilterType
			value: string | string[]
		}

		if (type === FILTER_TYPES.NUMBER) {
			const filterValues = Array.isArray(value)
				? value.map(String)
				: String(value)
						.split(',')
						.map((v) => v.trim())
			const statusValue = String(row.original.processingStatus ?? -1)

			if (row.original.objectType === 1) {
				return false
			}

			return filterValues.includes(statusValue)
		}

		return true
	}
}

// admin pages:
export const useAdminFilterUsers = (
	user: any,
	filterType: AdminUserFilterType
): boolean => {
	const isExpired = isInviteExpired(user?.invite)

	switch (filterType) {
		case 'admins':
			return user?.roles?.some((r: any) => r?.isAdministrator)
		case 'not-admins':
			return user?.roles?.every((r: any) => !r?.isAdministrator)
		case 'active':
			return !user?.isDeactivated
		case 'not-active':
			return user?.isDeactivated
		case 'invited':
			return user?.invite && !user?.invite?.acceptedAt && !isExpired
		case 'invite-expired':
			return isExpired
		case 'all':
		default:
			return true
	}
}
